import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Copy, Globe, ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import { createSite, type Site } from '../../api/client';

export default function SetupPage() {
  const navigate = useNavigate();
  const [domain, setDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [site, setSite] = useState<Site | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleAddSite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = domain.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!trimmed) {
      setError('Please enter a domain');
      return;
    }

    setIsSubmitting(true);
    try {
      const newSite = await createSite({ name: trimmed, domain: trimmed });
      setSite(newSite);
    } catch {
      setError('Failed to create site. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const snippet = site
    ? `<script src="https://vantage-api.foober.workers.dev/tracker.js"></script>
<script>
  Vantage.init({ siteId: '${site.id}', serverUrl: 'https://vantage-api.foober.workers.dev' });
</script>`
    : '';

  function handleCopy() {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-gray-50 px-4 py-16">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {site ? 'Install the tracking code' : 'Payment successful!'}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {site
              ? 'Add this snippet to your website to start tracking'
              : 'Add your first website to get started with Vantage'}
          </p>
        </div>

        {!site ? (
          /* Step 1: Add domain */
          <form onSubmit={handleAddSite} className="card space-y-5">
            {error && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <div>
              <label htmlFor="domain" className="block text-sm font-medium text-gray-700">
                Website domain
              </label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Globe className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="domain"
                  type="text"
                  required
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="input pl-10"
                  placeholder="example.com"
                />
              </div>
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? 'Creating site...' : 'Add Website'}
            </button>
          </form>
        ) : (
          /* Step 2: Show tracking code */
          <div className="card space-y-5">
            <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3">
              <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
              <p className="text-sm font-medium text-green-700">
                Site <span className="font-semibold">{site.domain}</span> created successfully
              </p>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Tracking code</label>
                <button
                  onClick={handleCopy}
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                    copied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  )}
                >
                  <Copy className="h-3 w-3" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
                <code>{snippet}</code>
              </pre>
            </div>

            <p className="text-xs text-gray-500">
              Paste this snippet before the closing <code className="rounded bg-gray-100 px-1 py-0.5 text-gray-700">&lt;/head&gt;</code> tag on every page you want to track.
            </p>

            <button
              onClick={() => navigate('/')}
              className="btn-primary flex w-full items-center justify-center gap-2"
            >
              Go to Dashboard
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
